import { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { VariableSizeList } from "react-window";
import CalendarListItem from "app/pages/.shared/CalendarList/CalendarListItem";
import classNames from "classnames";
import memoizeOne from "memoize-one";
import PropTypes from "prop-types";
import WeekDays from "app/pages/PrePackage/components/DatePickerCalendar/WeekDays";
import { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { getWeeksInMonth } from "date-fns";
import "./CalendarList.scss";
import indexOf from "lodash/indexOf";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import useOnDir from "app/utils/hooks/useOnDir";

const createItemData = memoizeOne(
	(
		months,
		selectedDate,
		handleDateClick,
		departureDateMin,
		departureDateMax,
		displayCalendarIcon,
		checkDateAvailability
	) => ({
		months,
		selectedDate,
		handleDateClick,
		departureDateMin,
		departureDateMax,
		displayCalendarIcon,
		checkDateAvailability,
	})
);

const itemKey = (index, data) => {
	const item = data.months[index];
	return item;
};

export const TABLET_ROW_GAP = 24;
export const TABLET_TILE_HEIGHT = 32;
const TABLET_MONTH_TITLE_HEIGHT = 56;
const TABLET_MONTH_PADDING = 16;
export const TABLET_MONTH_CHROME_HEIGHT = TABLET_MONTH_TITLE_HEIGHT + 2 * TABLET_MONTH_PADDING;

// Single source for the tablet grid sizes: the SCSS reads these variables, getItemSize uses the constants.
export const TABLET_GRID_STYLE = {
	"--tablet-tile-height": `${TABLET_TILE_HEIGHT}px`,
	"--tablet-row-gap": `${TABLET_ROW_GAP}px`,
	"--tablet-month-title-height": `${TABLET_MONTH_TITLE_HEIGHT}px`,
	"--tablet-month-padding": `${TABLET_MONTH_PADDING}px`,
};

const CalendarList = ({
	onDateSelect,
	months = [],
	selectedDate = [],
	departureDateMin,
	departureDateMax,
	heightOffset,
	displayCalendarIcon = true,
	checkDateAvailability = () => {},
	isCompact = false,
}) => {
	const outerRef = useRef(null);
	const [windowHeight, setWindowHeight] = useState(window.innerHeight);
	const direction = useOnDir();

	const getTopPositionForIndex = targetIndex => {
		let topPosition = 0;
		for (let index = 0; index < targetIndex; index += 1) {
			topPosition += getItemSize(index);
		}
		return topPosition;
	};

	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL;
	const isTablet = resolution === RESOLUTION.MEDIUM;

	useEffect(() => {
		const el = outerRef.current;
		if (el) {
			const dateToCenter = selectedDate[0] || new Date();
			const monthAndYear = `${dateToCenter.getMonth()}/${dateToCenter.getFullYear()}`;
			const monthIndex = indexOf(months, monthAndYear);

			el.scrollTo({
				behavior: "auto",
				top: getTopPositionForIndex(monthIndex),
			});
		}
	}, []);

	useEffect(() => {
		const handleResize = () => {
			setWindowHeight(window.innerHeight);
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const itemData = createItemData(
		months,
		selectedDate,
		onDateSelect,
		departureDateMin,
		departureDateMax,
		displayCalendarIcon,
		checkDateAvailability
	);

	// 270px is the height of all components in the side panel except the datepicker calendar
	const calendarContainerHeight = windowHeight - heightOffset;

	const getItemSize = useCallback(
		index => {
			const month = months[index];

			if (month) {
				const monthValue = Number(month.split("/")[0]);
				const yearValue = Number(month.split("/")[1]);
				const date = new Date(yearValue, monthValue, 15, 15, 0, 0);
				const weeksInMonth = getWeeksInMonth(date, { weekStartsOn: 1 });

				if (isTablet) {
					return (
						TABLET_MONTH_CHROME_HEIGHT +
						weeksInMonth * TABLET_TILE_HEIGHT +
						(weeksInMonth - 1) * TABLET_ROW_GAP
					);
				}

				// isCompact tiles are narrower than mobile's (card width / 7 instead of viewport
				// width / 7), so they're shorter too (aspect-ratio: 1/1) — reusing mobile's 400/430
				// leaves a large gap after the actual (shorter) content. At 332px wide,
				// div.react-calendar__month-view__days measures 277.11 for a 5-week month and
				// 332.53 for a 6-week one. +16px on top of the measured content height for
				// breathing room between months.
				if (isCompact) {
					return weeksInMonth === 6 ? 384 : 320;
				}

				if (weeksInMonth === 6) {
					return isMobile ? 430 : (windowHeight + 40) / 1.6;
				}

				return isMobile ? 400 : windowHeight / 1.6;
			}
			return 0;
		},
		[months, isMobile, isCompact, isTablet, windowHeight]
	);

	return (
		<div
			className={classNames("date-picker-calendar", {
				"date-picker-calendar--tablet": isTablet,
			})}
			style={isTablet ? TABLET_GRID_STYLE : undefined}
		>
			<WeekDays
				variant={isTablet ? TYPOGRAPHY_VARIANTS.SMALL : TYPOGRAPHY_VARIANTS.REGULAR}
			/>
			<VariableSizeList
				outerRef={outerRef}
				overscanCount={1}
				itemCount={months.length}
				height={calendarContainerHeight}
				estimatedItemSize={375}
				itemSize={getItemSize}
				itemData={itemData}
				itemKey={itemKey}
				direction={direction}
			>
				{CalendarListItem}
			</VariableSizeList>
		</div>
	);
};

CalendarList.propTypes = {
	onDateSelect: PropTypes.func,
	months: PropTypes.array,
	selectedDate: PropTypes.array,
	displayCalendarIcon: PropTypes.bool,
	departureDateMin: PropTypes.number,
	departureDateMax: PropTypes.number,
	heightOffset: PropTypes.number,
	checkDateAvailability: PropTypes.func,
	isCompact: PropTypes.bool,
};

export default memo(CalendarList);
